import React, { useState, useEffect } from "react";
import { auth, db } from "../firebase";
import { signOut } from "firebase/auth";
import { doc, onSnapshot, collection, query, where } from "firebase/firestore";
import Deposit from "./Deposit";
import Market from "./Market";
import Trades from "./Trades";
import Terms from "./Terms";
import DisclaimerPopup from "./DisclaimerPopup";
import Profile from "./Profile";
import Withdrawal from "./Withdrawal";
import Settings from "./Settings";

const COMMODITIES = ["XAUUSD", "XAGUSD", "USOIL", "XPTUSD", "NATGAS", "COPPER", "COCOA", "CORN"];

const calcPnl = (trade, currentPrice) => {
  if (!trade.entryPrice || !currentPrice) return 0;
  const priceDiff = trade.direction === "buy"
    ? currentPrice - trade.entryPrice
    : trade.entryPrice - currentPrice;
  return parseFloat(((priceDiff / trade.entryPrice) * trade.amount * trade.leverage).toFixed(2));
};

export default function Dashboard({ user }) {
  const [wallet, setWallet] = useState({ balance: 0, pnl: 0, credit: 0 });
  const [page, setPage] = useState("market");
  const [openTrades, setOpenTrades] = useState([]);
  const [adminPrices, setAdminPrices] = useState({});
  const [livePrices, setLivePrices] = useState({});

  useEffect(() => {
    const ref = doc(db, "wallets", user.uid);
    const unsub = onSnapshot(ref, (snap) => {
      if (snap.exists()) setWallet(snap.data());
    });
    const params = new URLSearchParams(window.location.search);
    if (params.get("deposit") === "success") {
      window.history.replaceState({}, "", "/");
    }
    return unsub;
  }, [user]);

  useEffect(() => {
    const q = query(collection(db, "trades"), where("userId", "==", user.uid), where("status", "==", "open"));
    const unsub = onSnapshot(q, (snap) => {
      setOpenTrades(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, [user]);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, "settings", "prices"), (snap) => {
      if (snap.exists()) setAdminPrices(snap.data());
    });
    return unsub;
  }, []);

  useEffect(() => {
    const newPrices = {};
    openTrades.forEach(t => {
      if (COMMODITIES.includes(t.asset) && adminPrices[t.asset]?.price > 0) {
        newPrices[t.asset] = adminPrices[t.asset].price;
      }
    });
    if (Object.keys(newPrices).length > 0) {
      setLivePrices(prev => ({ ...prev, ...newPrices }));
    }
  }, [adminPrices, openTrades]);

  useEffect(() => {
    const nonCommodityTrades = openTrades.filter(t => !COMMODITIES.includes(t.asset));
    if (nonCommodityTrades.length === 0) return;

    const fetchPrices = async () => {
      const cryptoMap = { BTCUSD: "bitcoin", ETHUSD: "ethereum", ADAUSD: "cardano", BNBUSD: "binancecoin", SOLUSD: "solana" };
      const forexMap = { EURUSD: "EUR", GBPUSD: "GBP", USDJPY: "JPY", AUDUSD: "AUD", USDCAD: "CAD", USDCHF: "CHF" };
      const stockMap = { AAPL: 175, TSLA: 180, GOOGL: 160, AMZN: 185, MSFT: 415, NVDA: 850 };
      const symbols = [...new Set(nonCommodityTrades.map(t => t.asset))];
      const newPrices = {};

      await Promise.all(symbols.map(async (sym) => {
        try {
          if (cryptoMap[sym]) {
            const res = await fetch(`https://api.coingecko.com/api/v3/simple/price?ids=${cryptoMap[sym]}&vs_currencies=usd`);
            const data = await res.json();
            newPrices[sym] = data[cryptoMap[sym]]?.usd || 0;
          } else if (forexMap[sym]) {
            const base = sym.slice(0, 3);
            const quote = sym.slice(3, 6);
            const res = await fetch(`https://api.exchangerate-api.com/v4/latest/${base}`);
            const data = await res.json();
            newPrices[sym] = base === "USD" ? data.rates[quote] : data.rates["USD"];
          } else if (stockMap[sym]) {
            newPrices[sym] = stockMap[sym];
          }
        } catch (e) {}
      }));
      setLivePrices(prev => ({ ...prev, ...newPrices }));
    };

    fetchPrices();
    const interval = setInterval(fetchPrices, 5000);
    return () => clearInterval(interval);
  }, [openTrades]);

  const floatingPnl = openTrades.reduce((sum, t) => {
    const cp = livePrices[t.asset];
    return sum + (cp ? calcPnl(t, cp) : 0);
  }, 0);

  const marginUsed = openTrades.reduce((sum, t) => sum + (t.amount || 0), 0);
  const equity = (wallet.balance || 0) + floatingPnl;
  const freeMargin = equity - marginUsed;
  const marginLevel = marginUsed > 0 ? ((equity / marginUsed) * 100) : 0;

  return (
    <div className="dashboard">
      <DisclaimerPopup />

      <div className="topbar">
        <div className="brand">TROY TRADE</div>
        <div className="topbar-nav">
          <button className={`nav-btn ${page === "market" ? "active" : ""}`} onClick={() => setPage("market")}>Market</button>
          <button className={`nav-btn ${page === "trades" ? "active" : ""}`} onClick={() => setPage("trades")}>My Trades</button>
          <button className={`nav-btn ${page === "deposit" ? "active" : ""}`} onClick={() => setPage("deposit")}>Deposit</button>
          <button className={`nav-btn ${page === "withdrawal" ? "active" : ""}`} onClick={() => setPage("withdrawal")}>Withdraw</button>
          <button className={`nav-btn ${page === "terms" ? "active" : ""}`} onClick={() => setPage("terms")}>Terms</button>
          <button className={`nav-btn ${page === "profile" ? "active" : ""}`} onClick={() => setPage("profile")}>Profile & KYC</button>
          <button className={`nav-btn ${page === "settings" ? "active" : ""}`} onClick={() => setPage("settings")}>Settings</button>
        </div>
        <div style={{display:"flex", alignItems:"center", gap:"14px"}}>
          <div className="wallet-info">
            <span className="wallet-label">Balance</span>
            <span className="wallet-value">${wallet.balance?.toFixed(2) || "0.00"}</span>
          </div>
          <div className="wallet-info">
            <span className="wallet-label">Equity</span>
            <span className="wallet-value" style={{color: floatingPnl >= 0 ? "#00C896" : "#FF3B5C"}}>
              ${equity.toFixed(2)}
            </span>
          </div>
          {marginUsed > 0 && (
            <>
              <div className="wallet-info">
                <span className="wallet-label">Margin</span>
                <span className="wallet-value" style={{color:"#5A9FFF"}}>${marginUsed.toFixed(2)}</span>
              </div>
              <div className="wallet-info">
                <span className="wallet-label">Free Margin</span>
                <span className="wallet-value" style={{color: freeMargin < 0 ? "#FF3B5C" : "#E8EAF0"}}>
                  ${freeMargin.toFixed(2)}
                </span>
              </div>
            </>
          )}
          {wallet.credit > 0 && (
            <div className="wallet-info">
              <span className="wallet-label">Credit</span>
              <span className="wallet-value" style={{color:"#9B6DFF"}}>${wallet.credit?.toFixed(2) || "0.00"}</span>
            </div>
          )}
          <button className="btn-logout" onClick={() => signOut(auth)}>Logout</button>
        </div>
      </div>

      {marginUsed > 0 && marginLevel < 100 && marginLevel > 0 && (
        <div style={{background:"rgba(255,59,92,0.12)", borderBottom:"1px solid rgba(255,59,92,0.3)", padding:"8px 24px", fontSize:"12px", color:"#FF3B5C", textAlign:"center", fontWeight:"600"}}>
          ⚠️ MARGIN CALL WARNING — Margin Level: {marginLevel.toFixed(0)}% — Your positions may be auto-closed if equity reaches $0
        </div>
      )}

      <div className="page-content">
        {page === "market" && <Market user={user} wallet={{...wallet, equity, freeMargin}} />}
        {page === "trades" && <Trades user={user} wallet={wallet} />}
        {page === "deposit" && <Deposit user={user} onBack={() => setPage("market")} />}
        {page === "withdrawal" && <Withdrawal user={user} wallet={wallet} />}
        {page === "terms" && <Terms onBack={() => setPage("market")} />}
        {page === "profile" && <Profile user={user} />}
        {page === "settings" && <Settings user={user} />}
      </div>

      <div className="footer">
        <div className="footer-text">
          © 2025 Troy Trade Investment | PT. Artemis Bosphora Arthagena | Reg. 8426105-4 | License HE433420
        </div>
        <div className="footer-links">
          <span onClick={() => setPage("terms")} style={{cursor:"pointer", color:"#F0A500"}}>Terms & Conditions</span>
          <span style={{margin:"0 8px", color:"#1E2840"}}>|</span>
          <a href="mailto:ptartemisbosphoraarthagena@gmail.com" style={{color:"#5A6480"}}>Support</a>
          <span style={{margin:"0 8px", color:"#1E2840"}}>|</span>
          <a href="http://artemisbosphora.com" target="_blank" rel="noreferrer" style={{color:"#5A6480"}}>artemisbosphora.com</a>
        </div>
        <div className="footer-risk">
          ⚠️ Risk Warning: Trading leveraged products carries high risk. You may lose more than your initial investment. PT. Artemis Bosphora Arthagena does not provide financial advice.
        </div>
      </div>
    </div>
  );
}